import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Gavel, Search as SearchIcon, PlusCircle, Clock, Store } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_META, ListingCard } from "@/pages/Marketplace";

// Auctions is a slim variant of the Marketplace surface — same visual
// language, but every listing here is an auction. Users bid instead of
// buying at a fixed price. Sort defaults to "ending_soon" because the
// natural read of an auction list is chronological urgency.

export default function Auctions() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [sellerType, setSellerType] = useState(params.get("seller_type") || "");
  const [sort, setSort] = useState(params.get("sort") || "ending_soon");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Persist filters into URL so shares/reloads land on the same view.
  useEffect(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (sellerType) p.set("seller_type", sellerType);
    if (sort !== "ending_soon") p.set("sort", sort);
    setParams(p, { replace: true });
  }, [q, category, sellerType, sort, setParams]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (q) qs.set("q", q);
        if (category) qs.set("category", category);
        if (sellerType) qs.set("seller_type", sellerType);
        qs.set("sort", sort);
        qs.set("listing_type", "auction");
        const r = await api.get(`/marketplace/listings?${qs.toString()}`);
        if (!alive) return;
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail));
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [q, category, sellerType, sort]);

  const endingSoonCount = useMemo(() => {
    const now = Date.now();
    return items.filter((l) => {
      if (!l.ends_at) return false;
      const ms = new Date(l.ends_at).getTime() - now;
      return ms > 0 && ms < 24 * 3600 * 1000;
    }).length;
  }, [items]);

  return (
    <div className="min-h-[100vh]" data-testid="auctions-page">
      {/* Hero header */}
      <div className="relative overflow-hidden border-b border-[#EAE3DE] bg-gradient-to-br from-[#FDF3F1] via-white to-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#F0C9C4] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#B31E1E]">
                <Gavel size={12} /> Auctions
              </div>
              <h1 className="mt-3 text-4xl md:text-5xl font-black tracking-tight text-[#1B1614] leading-[1.05]">
                Bid, don&rsquo;t buy.
              </h1>
              <p className="mt-3 text-[15px] md:text-base leading-relaxed text-[#5C4E48] max-w-lg">
                Timed auctions from your neighbours. Highest bid at the deadline wins — no reserve unless the seller sets one.
                {endingSoonCount > 0 && (
                  <span className="block mt-2 text-[#B31E1E] font-semibold">
                    <Clock size={12} className="inline mr-1" />
                    {endingSoonCount} ending in the next 24 hours
                  </span>
                )}
              </p>
            </div>
            {user?.role === "customer" && (
              <Link to="/marketplace/sell?type=auction" data-testid="auctions-sell-cta">
                <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]">
                  <PlusCircle size={14} className="mr-1.5" /> Start an auction
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="auctions-filters">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search auctions"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-sm pl-9"
              data-testid="auctions-search-input"
            />
          </div>
          <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="auctions-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_META).map(([k, label]) => (
                <SelectItem key={k} value={k}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sellerType || "all"} onValueChange={(v) => setSellerType(v === "all" ? "" : v)}>
            <SelectTrigger className="w-36 rounded-sm" data-testid="auctions-seller-filter">
              <SelectValue placeholder="Seller" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              <SelectItem value="customer">Neighbours</SelectItem>
              <SelectItem value="business">
                <span className="inline-flex items-center gap-1"><Store size={11} /> Businesses</span>
              </SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="auctions-sort-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ending_soon">Ending soon</SelectItem>
              <SelectItem value="recent">Newest</SelectItem>
              <SelectItem value="price_asc">Current bid: low → high</SelectItem>
              <SelectItem value="price_desc">Current bid: high → low</SelectItem>
              <SelectItem value="popular">Most viewed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="auctions-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="rounded-md overflow-hidden">
                <div className="aspect-square bg-gray-100 animate-pulse" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4" />
                </div>
              </Card>
            ))}
          </div>
        ) : items.length === 0 ? (
          <Card className="rounded-md p-10 text-center" data-testid="auctions-empty">
            <Gavel className="mx-auto mb-2 text-gray-400" size={32} />
            <p className="text-sm text-gray-600">
              No live auctions right now.{" "}
              {user
                ? <Link to="/marketplace/sell?type=auction" className="text-[#B31E1E] hover:underline">Start one</Link>
                : <Link to="/register" className="text-[#B31E1E] hover:underline">Sign up to start one</Link>}
              .
            </p>
            <p className="mt-3 text-xs text-gray-500">
              Looking for buy-it-now?{" "}
              <Link to="/marketplace" className="text-[#B31E1E] hover:underline">Browse the marketplace</Link>.
            </p>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="auctions-grid">
              {items.map((l) => <ListingCard key={l.id} l={l} />)}
            </div>
            <div className="mt-4 text-xs font-mono text-gray-400">
              Showing {items.length} of {total}
            </div>
          </>
        )}

        <div className="mt-10 text-center">
          <Badge className="rounded-sm bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-100 font-normal">
            Buy-it-now listings live at{" "}
            <Link to="/marketplace" className="ml-1 text-[#B31E1E] hover:underline">/marketplace</Link>
          </Badge>
        </div>
      </div>
    </div>
  );
}
